<template>
  <div
    v-spinner="realLoading"
    class="mm-table-wrapper">
    <div
      v-if="Boolean($slots.header)"
      class="mm-table__header">
      <slot name="header"></slot>
    </div>
    <div
      ref="tableWrapper"
      :class="['mm-table', `mm-table__${size}`, `mm-table__${theme}`]"
      :style="wrapperStyle">
      <mm-thead
        v-if="!!realHeight"
        ref="thead"
        v-bind="theadProps"
        :columns="localColumns"></mm-thead>
      <mm-tbody
        ref="tbody"
        v-bind="tbodyProps"
        :columns="localColumns"
        :draggable="draggable"
        :set-table-layout="setTableLayout"
        @drop="handleDrop"></mm-tbody>
      <mm-tfixed
        v-if="leftColumns.length"
        :store="store"
        position="left">
        <mm-thead
          v-if="!!realHeight"
          :columns="leftColumns"
          v-bind="theadProps"
          fixed="left"></mm-thead>
        <mm-tbody
          v-bind="tbodyProps"
          :columns="leftColumns"
          fixed="left"></mm-tbody>
      </mm-tfixed>
      <mm-tfixed
        v-if="rightColumns.length"
        :store="store"
        position="right">
        <mm-thead
          v-if="!!realHeight"
          :columns="rightColumns"
          v-bind="theadProps"
          fixed="right"></mm-thead>
        <mm-tbody
          v-bind="tbodyProps"
          :columns="rightColumns"
          fixed="right"></mm-tbody>
      </mm-tfixed>
      <mm-tfooter
        v-if="store.state.filteredRows.length && summary"
        ref="tfooter"
        :tfooter-props="tfooterProps"
        :left-columns="leftColumns"
        :right-columns="rightColumns"
        :columns="localColumns"
        :store="store"></mm-tfooter>
    </div>
    <div
      v-if="Boolean($slots.footer)"
      class="mm-table__footer">
      <slot name="footer"></slot>
    </div>
    <mm-pagination
      v-if="!!pagination"
      ref="pagination"
      v-bind="pagination"></mm-pagination>
  </div>
</template>

<script>
import cloneDeep from "lodash/cloneDeep"
import isEqual from "lodash/isEqual"
import get from "lodash/get"

import spinner from "../../directives/spinner"

import locale from "../../mixins/locale"
import { t } from "../../locale"
import Pagination from "../pagination"

import Thead from "./thead"
import Tbody from "./tbody"
import Tfixed from "./tfixed"
import Tfooter from "./tfooter"
import Store from "./store"
import { getFlattenColumns, getScrollBarWidth } from "./utils"

import config from "../../managers/config"

config.defaults({
  "Table.size": "medium",
  "Table.draggable": false,
  "Table.theme": "striped",
  "Table.minColWidth": 80,
  "Table.minDragWidth": 0,
  "Table.checkboxColWidth": 60,
  "Table.columnDraggable": true,
})

export default {
  name: "mm-table",
  components: {
    "mm-pagination": Pagination,
    "mm-thead": Thead,
    "mm-tbody": Tbody,
    "mm-tfixed": Tfixed,
    "mm-tfooter": Tfooter,
  },
  directives: { spinner },
  mixins: [locale],
  provide() {
    return {
      "mm-table": this,
    }
  },
  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    dataSource: {
      type: Array,
      default: () => [],
    },
    selectedRows: {
      type: Array,
      default: () => [],
    },
    height: {
      type: Number,
      default: 0,
    },
    maxHeight: {
      type: Number,
    },
    width: {
      type: Number,
      default: 0,
    },
    size: {
      type: String,
      default: () => config.get("Table.size"),
    },
    rowSelection: {
      type: Object,
      default: null,
    },
    draggable: {
      type: Boolean,
      default: () => config.get("Table.draggable"),
    },
    spanMethod: Function,
    onClick: {
      type: Function,
      default: () => {},
    },
    rowKey: String,
    theme: {
      type: String,
      default: () => config.get("Table.theme"),
      validator() {
        return ["horizontal-bordered", "vertical-bordered", "bordered", "striped", "striped-bordered"]
      },
    },
    minColWidth: {
      type: Number,
      default: () => config.get("Table.minColWidth"),
    },
    minDragWidth: {
      type: Number,
      default: () => config.get("Table.minDragWidth"),
    },
    checkboxColWidth: {
      type: Number,
      default: () => config.get("Table.checkboxColWidth"),
    },
    rowClass: {
      type: [String, Object, Array, Function],
    },
    loading: {
      type: [Boolean, Object],
      default: false,
    },
    pagination: {
      type: [Object, Boolean],
      validator(val) {
        return val === false || typeof val === "object"
      },
    },
    columnDraggable: {
      type: Boolean,
      default: () => config.get("Table.columnDraggable"),
    },
    onSort: Function,
    summary: Boolean,
    summaryText: {
      type: String,
      default() {
        return t("ui.table.sumText")
      },
    },
    summaryRender: Function,
  },
  data() {
    return {
      leftColumns: [],
      rightColumns: [],
      localColumns: [],
      store: new Store(),
    }
  },
  computed: {
    showSelectCol() {
      return Boolean(this.rowSelection && this.rowSelection.onChange)
    },
    wrapperStyle() {
      return this.width
        ? {
          width: `${this.width}px`,
        }
        : {}
    },
    theadProps() {
      return {
        rowSelection: this.rowSelection,
        store: this.store,
        scrollHandler: this.scrollHandler,
      }
    },
    tbodyProps() {
      return {
        height: this.height,
        maxHeight: this.maxHeight,
        loading: this.loading,
        rowSelection: this.rowSelection,
        store: this.store,
        scrollHandler: this.scrollHandler,
        spanMethod: this.spanMethod,
        handleClick: this.handleClick,
        rowKey: this.rowKey,
        rowClass: this.rowClass,
        summary: this.summary,
      }
    },
    tfooterProps() {
      return {
        rowSelection: this.rowSelection,
        store: this.store,
        scrollHandler: this.scrollHandler,
        spanMethod: this.spanMethod,
        summaryText: this.summaryText,
        summaryRender: this.summaryRender,
        rowClass: this.rowClass,
      }
    },
    realHeight() {
      return this.height || this.maxHeight
    },
    realLoading() {
      if (typeof this.loading === "boolean") {
        return {
          visible: this.loading,
        }
      }

      return this.loading
    },
  },
  watch: {
    rowKey: {
      immediate: true,
      handler(value) {
        this.store.setState("rowKey", value)
      },
    },
    rowSelection: {
      immediate: true,
      handler(value) {
        this.store.setRowSelection(value)
      },
    },
    columns: {
      immediate: true,
      handler() {
        this.setLocalColumns()
      },
    },
    dataSource: {
      immediate: true,
      handler(value) {
        this.store.setState("rows", value)
      },
    },
    selectedRows: {
      immediate: true,
      handler(value = []) {
        const rows = this.rowKey ? value.map(item => get(item, this.rowKey)) : value
        if (!isEqual(rows, this.store.state.selectedRows)) {
          this.store.state.selectedRows = rows
        }
      },
    },
    "store.state.selectedRows": {
      immediate: true,
      handler(value = []) {
        if (!this.showSelectCol) {
          return
        }

        const { onChange } = this.rowSelection
        const { rowKey } = this
        const { enabledRows } = this.store.state
        const length = value.length
        let update = value
        if (rowKey) {
          update = this.dataSource.filter(item => value.includes(get(item, rowKey)))
        }

        this.$emit("update:selectedRows", update)
        onChange && onChange(update)

        this.store.state.selectAll = Boolean(
          length && length === enabledRows.length
        )
        this.store.state.indeterminate = Boolean(
          length && length < enabledRows.length
        )
      },
    },
    pagination: {
      immediate: true,
      handler() {
        const pageHeight = this.$refs.pagination ? this.$refs.pagination.$el.offsetHeight : 0
        const borderTop = ["bordered", "striped-bordered"].includes(this.theme) ? 1 : 0 // borderTop 是放在容器上的，会影响表格整体的高度
        this.store.setLayout("pageHeight", pageHeight + borderTop)
      },
    },
  },
  mounted() {
    this.initLayoutData()
  },
  methods: {
    initLayoutData() {
      this.store.setLayout("scrollBarWidth", getScrollBarWidth())
    },
    setLocalColumns() {
      let columns = cloneDeep(this.columns)
      let left = []
      let right = []
      let normal = []
      columns = this.groupedColumns(columns)

      columns.forEach(col => {
        switch (col.fixed) {
          case "left":
            left.push(col)
            break
          case "right":
            right.push(col)
            break
          default:
            normal.push(col)
        }
      })

      this.leftColumns = left
      this.rightColumns = right
      this.localColumns = [...left, ...normal, ...right]
      this.store.setState("columns", columns)
    },
    groupedColumns(columns, currentRow = 0, parentColumn = {}, rows = []) {
      rows[currentRow] = rows[currentRow] || []
      const grouped = []
      const setRowSpan = column => {
        const rowSpan = rows.length - currentRow
        if (
          column &&
          !column.children &&
          rowSpan > 1 &&
          (!column.rowSpan || column.rowSpan < rowSpan)
        ) {
          column.rowSpan = rowSpan
        }
      }

      columns.forEach((column, index) => {
        const newColumn = { ...column }
        rows[currentRow].push(newColumn)
        parentColumn.colSpan = parentColumn.colSpan || 0
        parentColumn.width = parentColumn.width || 0
        if (newColumn.children && newColumn.children.length > 0) {
          newColumn.children = this.groupedColumns(
            newColumn.children,
            currentRow + 1,
            newColumn,
            rows
          )
          parentColumn.colSpan += newColumn.colSpan
          parentColumn.width += newColumn.width
        } else {
          parentColumn.colSpan++
          parentColumn.width += newColumn.width
        }
        // update rowspan to all same row columns
        for (let i = 0; i < rows[currentRow].length - 1; ++i) {
          setRowSpan(rows[currentRow][i])
        }
        // last column, update rowspan immediately
        if (index + 1 === columns.length) {
          setRowSpan(newColumn)
        }
        grouped.push(newColumn)
      })

      return grouped
    },
    setTableLayout() {
      if (this.$isServer || !this.$el) {
        return
      }
      const columns = getFlattenColumns(this.localColumns)
      const { tbody, thead, pagination, tfooter } = this.$refs
      const headHeight = thead ? thead.$el.clientHeight : 0
      const pageHeight = pagination ? pagination.$el.clientHeight : 0
      const footHeight = tfooter ? tfooter.$el.clientHeight : 0
      const containerWidth = this.width || this.$refs.tableWrapper.clientWidth
      const maxHeight = this.realHeight ? (this.realHeight - headHeight - footHeight - pageHeight) : 0
      const scrollY = Boolean(
        maxHeight && tbody.$refs.tbody.clientHeight > maxHeight
      )
      const scrollBarWidth = scrollY
        ? tbody.$el.offsetWidth - tbody.$el.clientWidth
        : 0
      const selectColWidth = this.showSelectCol ? this.checkboxColWidth : 0
      const minColWidth = this.minColWidth

      const fixedWidth = columns
        .filter(
          col => !col.flex && typeof col.width === "number" && col.width > 0
        )
        .reduce((prev, next) => prev + next.width, 0)
      const flexColumns = columns.filter(
        col => !(typeof col.width === "number" && col.width > 0) || col.flex
      )
      const flexCount = flexColumns.length

      if (flexCount) {
        const restWidth =
          containerWidth - fixedWidth - selectColWidth - scrollBarWidth
        let average = Math.floor(restWidth / flexCount)
        average = average < minColWidth ? minColWidth : average
        const remainder = average < minColWidth ? 0 : restWidth % flexCount

        flexColumns.forEach((col, index) => {
          this.$set(col, "flex", true)
          this.$set(col, "width", index === 0 ? average + remainder : average)
        })
      }

      const tableWidth = columns.reduce(
        (prev, next) => prev + next.width,
        selectColWidth
      )
      const scrollX = tableWidth > containerWidth - scrollBarWidth

      this.store.setState("flattenColumns", columns)
      this.store.setScrollStatus(scrollX, scrollY)
      this.store.setLayout("containerWidth", containerWidth)
      this.store.setLayout("tableWidth", tableWidth)
      this.store.setLayout("selectColWidth", selectColWidth)
    },
    scrollHandler(e) {
      const { scrollTop, scrollLeft } = e.target
      const { scrollX, scrollY } = this.store.layout
      const {
        thead,
        tbody,
        tfooter,
        fixedTbody,
        rightFixedTbody,
      } = this.store.refs

      if (e.target === thead && scrollLeft !== scrollX) {
        tbody.scrollLeft = scrollLeft
        tfooter && (tfooter.scrollLeft = scrollLeft)
        this.store.setScroll(scrollLeft, "x")
      }

      if (e.target === tbody && scrollLeft !== scrollX) {
        thead && (thead.scrollLeft = scrollLeft)
        tfooter && (tfooter.scrollLeft = scrollLeft)
        this.store.setScroll(scrollLeft, "x")
      }

      if (e.target === tfooter && scrollLeft !== scrollX) {
        thead && (thead.scrollLeft = scrollLeft)
        tbody.scrollLeft = scrollLeft
        this.store.setScroll(scrollLeft, "x")
      }

      if (e.target !== thead && e.target !== tfooter && scrollTop !== scrollY) {
        tbody.scrollTop = scrollTop
        fixedTbody && (fixedTbody.scrollTop = scrollTop)
        rightFixedTbody && (rightFixedTbody.scrollTop = scrollTop)
        this.store.setScroll(scrollTop, "y")
      }
    },
    handleDrop(...args) {
      this.$emit("drop", ...args)
    },
    handleClick(...args) {
      this.$emit("click", ...args)
      this.onClick && this.onClick(...args)
    },
  },
}
</script>
